import type { Region, ScopeAdapter, Tenant } from '@vextura/shell';
import { getConsoleConfig } from './loadConfig.js';

/**
 * Resolves tenants from vex-iam and regions from vex-config RIP. Used in
 * production. When @vextura/api-clients-* lands in item D, swap fetch() for
 * typed Smithy calls.
 */
export const httpScopeAdapter: ScopeAdapter = {
  async listTenants() {
    const { gateUrl } = getConsoleConfig();
    const res = await fetch(`${gateUrl}/iam/tenants`, { credentials: 'include' });
    if (!res.ok) return [];
    const body = (await res.json()) as { tenants?: ReadonlyArray<Tenant> };
    return body.tenants ?? [];
  },
  async listRegions() {
    const { gateUrl } = getConsoleConfig();
    const res = await fetch(`${gateUrl}/config/regions`, { credentials: 'include' });
    if (!res.ok) return [];
    const body = (await res.json()) as { regions?: ReadonlyArray<Region> };
    return body.regions ?? [];
  },
};

interface DevScopeSeed {
  tenants: ReadonlyArray<Tenant>;
  regions: ReadonlyArray<Region>;
}

/**
 * Dev fallback. Reads /dev-scope.json from the static asset directory. The
 * operator can edit that file (or replace it per environment) without
 * rebuilding. Absent file → empty list; the UI surfaces this via an empty
 * TenantSwitcher dropdown.
 */
export const offlineScopeAdapter: ScopeAdapter = {
  async listTenants() {
    return loadSeed().then((s) => s.tenants);
  },
  async listRegions() {
    return loadSeed().then((s) => s.regions);
  },
};

let seedPromise: Promise<DevScopeSeed> | null = null;
async function loadSeed(): Promise<DevScopeSeed> {
  if (!seedPromise) {
    seedPromise = fetch('/dev-scope.json')
      .then((res) => (res.ok ? (res.json() as Promise<DevScopeSeed>) : { tenants: [], regions: [] }))
      .catch(() => ({ tenants: [], regions: [] }));
  }
  return seedPromise;
}
